import { Trans } from 'react-i18next';
import i18n from 'locales/i18n';
import {
	BoldVerticalBarChartData,
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import { RadialBarChartProps } from '@components/report/chart/radial-bar-chart/RadialBarChart';

import { formatBoldVerticalChartData } from '@components/report/chart/utils';
import { VideoContentItemProps } from '@components/report/content-items/video-content-item/VideoContentItem';

import { YTContentData, YTReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { AdContentProps } from './AdContent';

// import { VideoContentYtProps } from './VideoContentYt';

export class AdContentRateAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get adContentCount() {
		return this.value?.content?.ad_content_summary?.ad_content_count ?? undefined;
	}
	get totalContentCount() {
		return this.value?.content?.ad_content_summary?.total_content_count ?? undefined;
	}
	get adContentRate() {
		return this.value?.content?.ad_content_summary?.ad_content_rate ?? 0;
	}

	adapt(): RadialBarChartProps {
		return {
			value: this.adContentRate ? this.adContentRate * 100 : undefined,
			legends: [
				{
					color: 'primary',
					label:
						i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-rate.radial-bar-chart.ad-content',
						) ?? '', //'광고 콘텐츠',
					value: this.adContentCount
						? i18n?.t('unit.counts', { number: round(this.adContentCount, 'down') })
						: undefined,
					isMain: true,
				},
				{
					color: 'gray',
					label:
						i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-rate.radial-bar-chart.recent-content',
						) ?? '',
					value: this.totalContentCount
						? i18n?.t('unit.counts', {
								number: round(this.totalContentCount, 'down'),
						  })
						: undefined,
				},
			],
			isPositive: true,
		};
	}
}

export class AvgAdContentViewsAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	/**
	 * @description 광고 콘텐츠 조회 수
	 */
	get avgAdContentView() {
		return this.value?.content?.ad_content_summary?.avg_ad_content_view ?? undefined;
	}
	/**
	 * @description 일반 콘텐츠 평균 조회 수
	 */
	get avgContentView() {
		return this.value?.content?.ad_content_summary?.avg_view ?? undefined;
	}

	/**
	 * @description 비교 결과 텍스트
	 */
	textMapper = {
		high: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.high-comparison"
				components={[
					<span key="high">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.high',
						)}
					</span>,
				]}
			/>
		),
		normal: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.normal-comparison"
				components={[
					<span key="normal">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.normal',
						)}
					</span>,
				]}
			/>
		),
		low: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.low-comparison"
				components={[
					<span key="low">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.low',
						)}
					</span>,
				]}
			/>
		),
		unmeasurable: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.unmeasurable-comparison"
				components={[
					<span key="unmeasurable">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-views-adapter.unmeasurable',
						)}
					</span>,
				]}
			/>
		),
	} as const;

	/**
	 * @description 비교 결과 상태
	 */
	get status() {
		const hasAllValue = this.avgAdContentView && this.avgContentView;
		if (!hasAllValue) return 'unmeasurable';

		return this.avgAdContentView > this.avgContentView * 1.1
			? 'high'
			: this.avgAdContentView < this.avgContentView * 0.9
			? 'low'
			: 'normal';
	}

	/**
	 * @description 인플루언서 광고 콘텐츠 조회 수 차트데이터 (주 데이터)
	 */
	get infAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.avgAdContentView && this.avgContentView;

		return {
			value: i18n?.t('unit.times', {
				number: round(this.avgAdContentView ?? 0, 'down'),
			}),
			label: this.value?.account?.profile?.username ?? '',
			valuePercentage: hasAllValue
				? (this.avgAdContentView / (this.avgAdContentView + this.avgContentView)) * 100
				: undefined,
			colorType: this.status === 'high' ? 'primary' : 'black',
			isMain: true,
		};
	}

	/**
	 * @description 인플루언서 일반 콘텐츠 조회 수 차트데이터 (비교군 데이터)
	 */
	get avgContentAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.avgAdContentView && this.avgContentView;

		return {
			value: i18n?.t('unit.times', { number: round(this.avgContentView ?? 0, 'down') }),
			label: i18n?.t(
				'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-content-view.label',
			), //'일반 콘텐츠 평균',
			valuePercentage: hasAllValue
				? (this.avgContentView / (this.avgAdContentView + this.avgContentView)) * 100
				: undefined,
			colorType: 'gray',
		};
	}

	get data(): BoldVerticalBarChartData[] {
		const avgAdContentViewDefaultData = {
			label: this.value?.account?.profile?.username ?? '',
		};
		const avgContentViewDefaultData = {
			label: i18n?.t(
				'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-content-view.label',
			),
		};

		const avgAdContentViewData = formatBoldVerticalChartData(
			this.avgAdContentView,
			avgAdContentViewDefaultData,
			this.infAdvancedData,
		);
		const avgContentViewData = formatBoldVerticalChartData(
			this.avgContentView,
			avgContentViewDefaultData,
			this.avgContentAdvancedData,
		);

		return [avgAdContentViewData, avgContentViewData];
	}

	get valueText(): string {
		if (!this.avgAdContentView)
			return (
				i18n?.t(
					'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-view.preparing',
				) ?? ''
			);

		return i18n?.t('unit.times', { number: round(this.avgAdContentView, 'down') }) ?? '';
	}

	adapt(): BoldVerticalBarChartProps {
		return {
			desc: (
				<Trans
					// t={i18n?.t}
					i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-view.description"
					components={[
						<span key="0">{this.valueText}</span>,
						this.textMapper[this.status],
					]}
				/>
			),
			data: this.data,
		};
	}
}

export class AvgAdContentEngagementAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	/**
	 * @description 광고 콘텐츠 참여율
	 */
	get avgAdContentEngagement() {
		return (
			this.value?.content?.ad_content_summary?.avg_ad_content_real_engagement ?? undefined
		);
	}
	/**
	 * @description 일반 콘텐츠 평균 참여율
	 */
	get avgContentEngagement() {
		return this.value?.content?.ad_content_summary?.real_engagement ?? undefined;
	}

	textMapper = {
		high: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.high-comparison"
				components={[
					<span key="high">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.high',
						)}
					</span>,
				]}
			/>
		),
		normal: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.normal-comparison"
				components={[
					<span key="normal">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.normal',
						)}
					</span>,
				]}
			/>
		),
		low: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.low-comparison"
				components={[
					<span key="low">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.low',
						)}
					</span>,
				]}
			/>
		),
		unmeasurable: (
			<Trans
				// t={i18n?.t}
				i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.unmeasurable-comparison"
				components={[
					<span key="unmeasurable">
						{i18n?.t(
							'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement-adapter.unmeasurable',
						)}
					</span>,
				]}
			/>
		),
	} as const;

	get status() {
		const hasAllValue = this.avgAdContentEngagement && this.avgContentEngagement;
		if (!hasAllValue) return 'unmeasurable';

		return this.avgAdContentEngagement > this.avgContentEngagement * 1.1
			? 'high'
			: this.avgAdContentEngagement < this.avgContentEngagement * 0.9
			? 'low'
			: 'normal';
	}

	/**
	 * @description 인플루언서 광고 콘텐츠 참여율 차트데이터 (주 데이터)
	 */
	get infAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.avgAdContentEngagement && this.avgContentEngagement;

		return {
			value: `${roundFloat(this.avgAdContentEngagement ?? 0, 'down', 2)}%`,
			label: this.value?.account?.profile?.username ?? '',
			valuePercentage: hasAllValue
				? (this.avgAdContentEngagement /
						(this.avgAdContentEngagement + this.avgContentEngagement)) *
				  100
				: undefined,
			colorType: this.status === 'high' ? 'primary' : 'black',
			isMain: true,
		};
	}

	/**
	 * @description 인플루언서 일반 콘텐츠 참여율 차트데이터 (비교군 데이터)
	 */
	get avgContentAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.avgAdContentEngagement && this.avgContentEngagement;

		return {
			value: `${roundFloat(this.avgContentEngagement ?? 0, 'down', 2)}%`,
			label: i18n?.t(
				'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement.label',
			),
			valuePercentage: hasAllValue
				? (this.avgContentEngagement /
						(this.avgAdContentEngagement + this.avgContentEngagement)) *
				  100
				: undefined,
			colorType: 'gray',
		};
	}

	get data(): BoldVerticalBarChartData[] {
		const avgAdContentEngagementDefaultData = {
			label: this.value?.account?.profile?.username ?? '',
		};
		const avgContentEngagementDefaultData = {
			label: i18n?.t(
				'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement.label',
			),
		};

		const avgAdContentEngagementData = formatBoldVerticalChartData(
			this.avgAdContentEngagement,
			avgAdContentEngagementDefaultData,
			this.infAdvancedData,
		);
		const avgContentEngagementData = formatBoldVerticalChartData(
			this.avgContentEngagement,
			avgContentEngagementDefaultData,
			this.avgContentAdvancedData,
		);

		return [avgAdContentEngagementData, avgContentEngagementData];
	}

	get valueText(): string {
		if (!this.avgAdContentEngagement)
			return (
				i18n?.t(
					'infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement.preparing',
				) ?? ''
			);

		return `${roundFloat(this.avgAdContentEngagement, 'down', 2)}%`;
	}

	adapt(): BoldVerticalBarChartProps {
		return {
			desc: (
				<Trans
					// t={i18n?.t}
					i18nKey="infReportYoutube:features.report.section-content.yt.ad-content.ad-content-adapter.avg-ad-content-engagement.description"
					components={[
						<span key="0">{this.valueText}</span>,
						this.textMapper[this.status],
					]}
				/>
			),
			data: this.data,
		};
	}
}

export class AdContentPostsAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get adContents() {
		return this.value?.content?.ad_content_summary?.ad_contents ?? [];
	}

	getVideoContentItemProps(videoContentData: YTContentData): VideoContentItemProps {
		return {
			outLink: videoContentData.content_link ?? undefined,
			imgSrc: videoContentData.content_img_link ?? undefined,
			viewCount: videoContentData.view_count ?? undefined,
			contentText: videoContentData.description ?? undefined,
			isPaidAd: videoContentData.is_ad ?? undefined,
			videoLength: videoContentData.duration ?? undefined,
		};
	}

	adapt(): AdContentProps['items'] {
		return this.adContents.map((videoContentData) =>
			this.getVideoContentItemProps(videoContentData),
		);
	}
}

export class AdContentAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	adapt(): AdContentProps {
		return {
			items: new AdContentPostsAdapter(this.value).adapt(),
			adContentRate: new AdContentRateAdapter(this.value).adapt(),
			averageAdContentViews: new AvgAdContentViewsAdapter(this.value).adapt(),
			averageAdContentEngagement: new AvgAdContentEngagementAdapter(this.value).adapt(),
		};
	}
}
